import React, { Component } from "react";
import * as d3 from "d3";
import { withFauxDOM } from "react-faux-dom";
import styled from "styled-components";
import d3SaveSvg from "d3-save-svg";

const StarStyled = styled.div`
  // border: 1px solid black;
  position: relative;
`;
const StarHeaderStyled = styled.div`
  color: black;
  left: 0;
  position: absolute;
  text-transform: capitalize;
  top: 5px;
  transform: translateX(
    ${props => (props.starView ? props.width / 2 - 15 : 0)}px
  );
  transition: transform 0.2s;
  transition-delay: 1s;
`;
const HelperMarker = styled.div`
  // border: 1px solid #666;
  border-radius: 100%;
  content: "";
  position: absolute;
  height: 10px;
  width: 10px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const rcps = [
  {
    value: 26,
    color: "#bbb"
    // color: "#9EFFE3"
  },
  {
    value: 34,
    color: "#999"
    // color: "#4C8D99"
  },
  {
    value: 45,
    color: "#666"
    // color: "#FF5B58"
  },
  {
    value: 60,
    color: "#000"
    // color: "#CC2870"
  }
];

const findPoint = (x, y, angle, distance) => {
  const result = {
    x: Math.cos((angle * Math.PI) / 180) * distance + x,
    y: Math.sin((angle * Math.PI) / 180) * distance + y
  };
  return result;
};

class StarChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialRender: true
    };

    this.innerRadius = 0;
    const x = d3.scaleLinear().range([0, this.innerRadius]);
    const sum = d3.sum(this.props.starData, d => d.value);
    this.xDomain = x.domain([0, sum]);
  }
  componentDidMount() {
    this.renderD3();
  }
  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.year !== prevProps.year ||
      this.state.initialRender !== prevState.initialRender ||
      this.props.starView !== prevProps.starView ||
      this.props.benchmarkSsp !== prevProps.benchmarkSsp ||
      this.props.showArcs !== prevProps.showArcs ||
      this.props.innerRadius !== prevProps.innerRadius ||
      this.props.armWidth !== prevProps.armWidth ||
      this.props.filled !== prevProps.filled
    ) {
      this.updateD3();
    }
  }

  createNodes = (numNodes, radius) => {
    const nodes = [];

    let angle = 0;
    let rotation = 0;
    let x = 0;
    let y = 0;
    let index = 0;

    for (index = 0; index < numNodes; index += 1) {
      if (!this.props.starView) {
        const xPos = 0;
        const yPos =
          (this.props.height / (numNodes + 3)) * index + this.props.height / 5;
        const rot = 0;
        nodes.push({ id: index, x: xPos, y: yPos, rotation: rot });
      } else {
        angle = (index / (numNodes / 2)) * Math.PI;
        x = radius * Math.cos(angle) + this.props.width / 2;
        y = radius * Math.sin(angle) + this.props.height / 2;
        rotation = (360 / numNodes) * index;

        nodes.push({ id: index, x, y, rotation });
      }
    }

    return nodes;
  };

  updateD3 = () => {
    const width = this.props.starView ? this.props.armWidth : 3;
    // reattach to faux dom
    const faux = this.props.connectFauxDOM("div", "chart");
    const svgDoc = d3.select(faux).select("svg");

    const numNodes = this.props.starData.length;
    const nodes = this.createNodes(numNodes, this.innerRadius);

    // inner start
    const innerStar = svgDoc
      .selectAll("rect.innerStar")
      .data(this.props.starData);
    innerStar.exit().remove();

    const newInnerStar = innerStar.enter().append("rect");
    newInnerStar
      .append("rect")
      .merge(innerStar)
      .attr("height", width)
      .attr("width", d => d.value)
      .attr("fill", "#999")
      .attr("stroke", "none")
      // .attr("fill", this.props.starView ? "#999" : "none")
      // .attr("stroke", this.props.starView ? "none" : "#999")
      .transition()
      .duration(650)
      .attr("x", (d, index) => nodes[index].x)
      .attr("y", (d, index) => nodes[index].y - width / 2)
      .attr("transform", (d, index) => {
        const { rotation } = nodes[index];
        return `rotate(${rotation} ${this.props.width / 2} ${this.props.width /
          2})`;
      });

    // ssp labels
    const sspLabel = svgDoc
      .selectAll("text.sspLabel")
      .data(this.props.starData);
    sspLabel.exit().remove();

    const newSspLabel = sspLabel.enter().append("text");
    newSspLabel
      .append("text")
      .merge(sspLabel)
      .transition()
      .duration(650)
      .attr(
        "x",
        (d, index) =>
          this.props.starView ? nodes[index].x + 30 : nodes[index].x - 1
      )
      .attr("y", (d, index) => nodes[index].y - this.props.armWidth + 5)
      .attr("transform", (d, index) => {
        const { rotation, x, y } = nodes[index];
        return `rotate(${rotation} ${x} ${y})`;
      });

    rcps.forEach((rcp, i) => {
      const rcpString = rcp.value.toString();

      // create rcps absolute
      const rcpAbsolute = svgDoc
        .selectAll(`rect.rcpAbsolute-${rcp.value}`)
        .data(this.props.starData);
      rcpAbsolute.exit().remove();
      const newRcpAbsolute = rcpAbsolute.enter().append("rect");
      newRcpAbsolute
        .append("rect")
        .merge(rcpAbsolute)
        .transition()
        .duration(650)
        .attr("height", this.props.armWidth)
        .attr("width", this.props.starView ? 0 : 2.5)
        .attr("x", (d, index) => {
          const xPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          return this.props.starView ? xPos.x : d.rcpValues[rcpString];
        })
        .attr("y", (d, index) => {
          const yPos = findPoint(
            this.props.width / 2,
            this.props.width / 2 - this.props.armWidth / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          return this.props.starView
            ? yPos.y
            : nodes[index].y - this.props.armWidth / 2;
        })
        .attr("transform", (d, index) => {
          const xPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          const yPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          return `rotate(${nodes[index].rotation} ${xPos.x} ${yPos.y})`;
        });

      // rcp lines
      const rcpIndicator = svgDoc
        .selectAll(`line.rcpIndicator-${rcp.value}`)
        .data(this.props.starData);
      rcpIndicator.exit().remove();
      const newRcpIndicator = rcpIndicator.enter().append("rect");
      newRcpIndicator
        .append("line")
        .merge(rcpIndicator)
        .transition()
        .duration(650)
        .style("strokeWidth", width)
        .attr("strokeLinecap", this.props.starView ? "round" : "butt")
        .attr(
          "stroke",
          d => (d.rcpValues[rcpString] >= d.value ? "#32db90" : "#e0787a")
        )
        .attr("x1", (d, index) => {
          const xPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.value
          );
          return this.props.starView ? xPos.x : d.value;
        })
        .attr("y1", (d, index) => {
          const yPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.value
          );
          return this.props.starView ? yPos.y : nodes[index].y;
        })
        .attr("x2", (d, index) => {
          const xPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          return this.props.starView ? xPos.x : d.rcpValues[rcpString];
        })
        .attr("y2", (d, index) => {
          const yPos = findPoint(
            this.props.width / 2,
            this.props.width / 2,
            nodes[index].rotation,
            d.rcpValues[rcpString]
          );
          return this.props.starView ? yPos.y : nodes[index].y;
        });
    });

    this.props.animateFauxDOM(1500);
  };

  renderD3 = () => {
    const width = this.props.armWidth;
    const numNodes = this.props.starData.length;
    const nodes = this.createNodes(numNodes, this.innerRadius);

    const faux = this.props.connectFauxDOM("div", "chart");

    // create svg
    const svgDoc = d3
      .select(faux)
      .append("svg")
      .attr("width", this.props.width)
      .attr("height", this.props.height)
      .attr("style", { backgroundColor: "#fff", display: "block" });

    // add data
    const starSvg = svgDoc
      .append("g")
      .selectAll("rect")
      .data(this.props.starData)
      .enter();

    // create inner star=
    starSvg
      .append("rect")
      .attr("class", "innerStar")
      .attr("fill", "#999")
      .attr("strokeWidth", 1)
      .attr("opacity", 1)
      .attr("x", (d, index) => nodes[index].x)
      .attr("y", (d, index) => nodes[index].y - this.props.armWidth / 2)
      .attr("height", width)
      .attr("width", d => d.value);

    // create ssp labels
    starSvg
      .append("text")
      .attr("class", "sspLabel")
      .attr("fill", "#999")
      .attr("fontSize", "10")
      .text(d => `SSP${d.ssp}`)
      .attr("x", (d, index) => nodes[index].x + 30)
      .attr("y", (d, index) => nodes[index].y - this.props.armWidth + 5);

    // create rcp deviation markers
    rcps.forEach((rcp, i) => {
      const rcpString = rcp.value.toString();

      starSvg
        .append("line")
        .attr("class", `rcpIndicator-${rcp.value}`)
        .attr("display", d => (d.rcpValues[rcpString] > 0 ? "block" : "none"))
        .attr(
          "stroke",
          d => (d.rcpValues[rcpString] >= d.value ? "#32db90" : "#e0787a")
        )
        .style("strokeWidth", 1.5)
        .attr("strokeLinecap", this.props.starView ? "round" : "butt")
        .attr("x1", d => d.value)
        .attr("y1", (d, index) => nodes[index].y)
        .attr("x2", d => d.rcpValues[rcpString])
        .attr("y2", (d, index) => nodes[index].y);

      // create rcps absolute
      starSvg
        .append("rect")
        .attr("class", `rcpAbsolute-${rcp.value}`)
        .attr("fill", rcps[i].color)
        .attr("display", d => (d.rcpValues[rcpString] > 0 ? "block" : "none"))
        .attr("x", d => d.rcpValues[rcpString])
        .attr("y", (d, index) => nodes[index].y - this.props.armWidth / 2)
        .attr("height", width)
        .attr("width", this.props.starView ? 0 : 2);
    });

    setTimeout(() => {
      this.setState({ initialRender: false });
    }, 2000);
  };

  render() {
    return (
      <StarStyled
        className="star-chart"
        onClick={event => {
          const star = event.target;
          const config = {
            filename: "SSPrint"
          };
          d3SaveSvg.save(star, config);
        }}
      >
        <StarHeaderStyled
          starView={this.props.starView}
          width={this.props.width}
        >
          {this.props.category}
        </StarHeaderStyled>
        {this.props.chart}
        <HelperMarker />
      </StarStyled>
    );
  }
}

StarChart.defaultProps = {
  chart: "loading"
};

export default withFauxDOM(StarChart);



// WEBPACK FOOTER //
// ./src/components/StarChart.js